import { useMemo } from 'react'
import { useQueries } from 'react-query'
import type { QueryKey, UseQueryOptions } from 'react-query'

interface QueriesIdleResult {
	data: undefined
	error: null
	isError: false
	isIdle: true
	isLoading: false
	isSuccess: false
	status: 'idle'
}

interface QueriesLoadingResult {
	data: undefined
	error: null
	isError: false
	isIdle: false
	isLoading: true
	isSuccess: false
	status: 'loading'
}

interface QueriesErrorResult<TError = unknown> {
	data: undefined
	error: TError
	isError: true
	isIdle: false
	isLoading: false
	isSuccess: false
	status: 'error'
}

interface QueriesSuccessResult<TData = unknown> {
	data: TData[]
	error: null
	isError: false
	isIdle: false
	isLoading: false
	isSuccess: true
	status: 'success'
}

type UseParallelQueries<TData = unknown, TError = unknown> =
	| QueriesIdleResult
	| QueriesErrorResult<TError>
	| QueriesLoadingResult
	| QueriesSuccessResult<TData>

export function useParallelQueries<TData, TError, TQueryKey extends QueryKey>(
	queries: UseQueryOptions<TData, TError, TData, TQueryKey>[]
): UseParallelQueries<TData, TError> {
	const paralellQueries = useQueries(queries)
	return useMemo(() => {
		const successResults: TData[] = []
		let isLoading: QueriesLoadingResult | undefined = undefined
		for (const query of paralellQueries) {
			switch (query.status) {
				case 'error': {
					const queryResult: QueriesErrorResult<TError> = {
						...query,
						data: undefined,
					}
					return queryResult
				}
				case 'idle':
					// How to handle 'idle` in the parallel query context is undertermined at the moment
					// We might want to handle it simialrly to `loading` or maybe just ignore it as we do now
					break
				case 'loading': {
					isLoading = query
					break
				}
				case 'success':
					successResults.push(query.data)
			}
		}
		if (isLoading) {
			return isLoading
		}
		return {
			data: successResults,
			error: null,
			isError: false,
			isIdle: false,
			isLoading: false,
			isLoadingError: false,
			isRefetchError: false,
			isSuccess: true,
			status: 'success',
		}
	}, [paralellQueries])
}
